import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { senderOf, to } from '../lib/nav'

import {
  ApiError,
  type InterpretationForm,
  type InterpretationItem,
  type InterpretationSaved,
  buildInterpretation,
  getInterpretation,
  saveInterpretation,
} from '../lib/api'

// The structured interpretation forms — echo, TEE and carotid.
//
//   oldcode/EchocardiographyInterpretationForm.aspx
//   oldcode/TeeInterpretationForm.aspx
//   oldcode/CarotidInterpretationForm.aspx
//
// One page for all three. The sections, labels, tooltips, dropdown choices and
// which controls the legacy actually persists are served as data by
// `GET /api/v1/studies/{id}/interpretation/{form}`; nothing about a particular
// form is known here — including the flattening, which is composed server-side
// so that Build and Save cannot drift apart.
//
// Two legacy behaviours reproduced because they are load-bearing:
//   * echo's Build **replaces** the interpretation box and the box is then
//     saved as typed, so a transcriptionist can edit the composed sentences.
//   * TEE has no such freedom: its save rebuilds the text and overwrites
//     whatever is in the box. The page says so before you type into it.
//
// One deliberately not reproduced: the legacy hides the whole checkbox panel
// from a transcriptionist (permission 5 without 4) and shows only the free-text
// box. That is a rendering rule with no server-side counterpart — the same user
// could always post the checkboxes anyway — so everything is shown here and the
// panel is collapsible instead.

type Checks = Record<string, boolean>
type Texts = Record<string, string>

interface Draft {
  checks: Checks
  texts: Texts
  selects: Texts
  interpretation: string
  dictatedNotRead: boolean
}

function draftFrom(form: InterpretationForm): Draft {
  const checks: Checks = {}
  const texts: Texts = {}
  const selects: Texts = {}
  for (const section of form.sections) {
    for (const item of section.items) {
      if (item.kind === 'check') {
        checks[item.name] = item.value === true
        if (item.textbox) texts[item.textbox] = ''
      } else if (item.kind === 'text') {
        texts[item.name] = String(item.value ?? '')
      } else {
        selects[item.name] = String(item.value ?? '')
      }
    }
  }
  return {
    checks,
    texts,
    selects,
    interpretation: form.interpretation,
    dictatedNotRead: form.dictated_not_read,
  }
}

function same(a: Draft, b: Draft) {
  return JSON.stringify(a) === JSON.stringify(b)
}

export default function InterpretationPage() {
  const { studyId, form } = useParams()
  const id = Number(studyId)
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [spec, setSpec] = useState<InterpretationForm | null>(null)
  const [base, setBase] = useState<Draft | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [showPanel, setShowPanel] = useState(true)

  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveStatus, setSaveStatus] = useState<number | null>(null)
  const [result, setResult] = useState<InterpretationSaved | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const s = await getInterpretation(id, String(form))
    setSpec(s)
    setBase(draftFrom(s))
    setDraft(draftFrom(s))
  }, [id, form])

  useEffect(() => {
    if (!Number.isFinite(id)) return
    setSpec(null)
    setBase(null)
    setDraft(null)
    setError(null)
    setSaveError(null)
    setResult(null)
    load().catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [id, load])

  const dirty = useMemo(
    () => Boolean(base && draft && !same(base, draft)),
    [base, draft],
  )

  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // The interpretation forms are modal in the legacy and have no navigation of
  // their own: `CarotidInterpretationForm.aspx` draws the banner and the rule
  // with nothing above them, and both Save (`:274`) and Cancel (`:283`) end in
  // `Response.Redirect(ViewState("SENDER"))`. Whoever opened the form is where
  // the form returns — the worklist for a transcriptionist
  // (`MainStudyList.aspx.vb:546`), the report hub for a physician who arrived
  // by Edit Interpretation (`StudyReport.aspx.vb:406`).
  const exit = senderOf(params, to('/', params))

  function leave(e: { preventDefault: () => void }) {
    if (!dirty) return true
    if (window.confirm('This interpretation has unsaved changes. Leave without saving?')) {
      return true
    }
    e.preventDefault()
    return false
  }

  // `btnClear_Click` on the echo and TEE forms (`…InterpretationForm.aspx.vb:548`)
  // sets `.Checked = False` on every checkbox and touches nothing else — not the
  // interpretation text, not Dictated Not Read. `btnReset_Click` on the carotid
  // form (:277), which has no checkbox table, clears `txtInterpretation` and
  // `chkDictatedNotRead` instead. Two different buttons doing two different
  // things, so both are reproduced rather than merged into one "clear".
  //
  // Neither touches the database in the legacy and neither does here: this is
  // draft state, undone by leaving without saving.
  function clearChecks() {
    touch()
    // Every key set false, **not** an emptied map: the save sends the whole form
    // the way a postback carries it, and an unticked box has to be written 0
    // rather than left alone. An empty object would silently mean "no boxes
    // submitted" and the save would leave the ticks standing.
    setDraft((d) =>
      d ? { ...d, checks: Object.fromEntries(Object.keys(d.checks).map((k) => [k, false])) } : d,
    )
  }

  function resetInterpretation() {
    touch()
    setDraft((d) => (d ? { ...d, interpretation: '', dictatedNotRead: false } : d))
  }

  function touch() {
    setResult(null)
    setSaveError(null)
    setSaveStatus(null)
  }

  function setCheck(name: string, value: boolean) {
    setDraft((d) => (d ? { ...d, checks: { ...d.checks, [name]: value } } : d))
    touch()
  }

  function setText(name: string, value: string) {
    setDraft((d) => (d ? { ...d, texts: { ...d.texts, [name]: value } } : d))
    touch()
  }

  function setSelect(name: string, value: string) {
    setDraft((d) => (d ? { ...d, selects: { ...d.selects, [name]: value } } : d))
    touch()
  }

  const ticked = useMemo(
    () => (draft ? Object.values(draft.checks).filter(Boolean).length : 0),
    [draft],
  )

  async function runBuild() {
    if (!draft || !spec) return
    setBusy(true)
    touch()
    try {
      const res = await buildInterpretation(id, spec.form, {
        checks: draft.checks,
        texts: draft.texts,
      })
      setDraft((d) => (d ? { ...d, interpretation: res.interpretation } : d))
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : String(e))
      setSaveStatus(e instanceof ApiError ? e.status : null)
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!draft || !spec) return
    setBusy(true)
    setSaveError(null)
    setSaveStatus(null)
    setResult(null)
    try {
      const res = await saveInterpretation(id, spec.form, {
        // The whole form, the way a postback carries it — an unticked box has
        // to be written 0, not left alone.
        checks: spec.table ? draft.checks : undefined,
        texts: draft.texts,
        selects: draft.selects,
        interpretation: draft.interpretation,
        dictated_not_read: draft.dictatedNotRead,
      })
      setResult(res)
      try {
        await load()
      } catch {
        /* the write is committed; a failed re-read is not a failed save */
      }
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : String(e))
      setSaveStatus(e instanceof ApiError ? e.status : null)
    } finally {
      setBusy(false)
    }
  }

  const header = <PageNav strip={null} />

  if (!Number.isFinite(id)) {
    return <main className="page"><p className="banner banner-error">Invalid study id.</p></main>
  }
  if (error) {
    return (
      <main className="page page-wide">
        {header}
        <p className="banner banner-error">{error}</p>
      </main>
    )
  }
  if (!spec || !draft) {
    return <main className="page"><p className="muted">Loading…</p></main>
  }

  const hasChecks = spec.sections.some((s) => s.items.some((i) => i.kind === 'check'))

  return (
    <main className="page page-wide">
      {header}

      <div className="page-head">
        <h1>{spec.title}</h1>
        <p className="subtle">
          {spec.legacy_page}
          {spec.table && <> · <code>{spec.table}</code> #{spec.interpretation_id ?? 'new'}</>}
          {' · '}<code>tblStudyReport</code> #{spec.report_id ?? '—'}
          {spec.study_type && <> · {spec.study_type}</>}
        </p>
      </div>

      {saveError && (
        <p className="banner banner-error">
          <strong>Not saved.</strong> {saveError}
          {saveStatus === 503 && ' Writes to the legacy database are disabled; nothing was changed.'}
        </p>
      )}
      {result && <SaveSummary result={result} />}

      {spec.note && <p className="banner banner-warn">{spec.note}</p>}

      {spec.table && spec.interpretation_id === null && (
        <p className="banner banner-warn">
          No <code>{spec.table}</code> row for this study yet. The legacy creates
          one the moment the page opens; here nothing is written until Save.
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        {hasChecks && (
          <>
            <div className="actions">
              <button type="button" className="link" onClick={() => setShowPanel((v) => !v)}>
                {showPanel ? 'Hide' : 'Show'} EZ-Interpretation ({ticked} ticked)
              </button>
            </div>
            {showPanel &&
              spec.sections
                .filter((s) => s.items.some((i) => i.kind === 'check'))
                .map((section) => (
                  <fieldset key={section.title}>
                    <legend>{section.title}</legend>
                    {section.note && <p className="hint">{section.note}</p>}
                    {section.items.map((item) => (
                      <CheckRow
                        key={item.name}
                        item={item}
                        checked={draft.checks[item.name] ?? false}
                        text={item.textbox ? draft.texts[item.textbox] ?? '' : ''}
                        disabled={busy}
                        onCheck={(v) => setCheck(item.name, v)}
                        onText={(v) => item.textbox && setText(item.textbox, v)}
                      />
                    ))}
                  </fieldset>
                ))}
          </>
        )}

        {spec.sections
          .filter((s) => s.items.every((i) => i.kind !== 'check'))
          .map((section) => (
            <fieldset key={section.title}>
              <legend>{section.title}</legend>
              {section.note && <p className="hint">{section.note}</p>}
              {section.items.map((item) =>
                item.kind === 'text' ? (
                  <div className="field" key={item.name}>
                    <label htmlFor={`ip-${item.name}`}>{item.label}</label>
                    <textarea
                      id={`ip-${item.name}`}
                      rows={item.rows ?? 4}
                      value={draft.texts[item.name] ?? ''}
                      disabled={busy}
                      onChange={(e) => setText(item.name, e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="field" key={item.name}>
                    <label htmlFor={`ip-${item.name}`}>
                      {item.label || <span className="muted">{item.column}</span>}
                    </label>
                    <select
                      id={`ip-${item.name}`}
                      value={draft.selects[item.name] ?? ''}
                      disabled={busy}
                      onChange={(e) => setSelect(item.name, e.target.value)}
                    >
                      {(item.choices ?? []).map((c) => (
                        <option key={c} value={c}>{c || ' '}</option>
                      ))}
                      {/* A value already on the row that is not one of the
                          legacy list items would otherwise vanish on save. */}
                      {!(item.choices ?? []).includes(String(draft.selects[item.name] ?? '')) && (
                        <option value={String(draft.selects[item.name])}>
                          {String(draft.selects[item.name])} (not a legacy choice)
                        </option>
                      )}
                    </select>
                  </div>
                ),
              )}
            </fieldset>
          ))}

        <fieldset>
          <legend>INTERPRETATION</legend>
          <div className="field field-check">
            <label>
              <input
                type="checkbox"
                checked={draft.dictatedNotRead}
                disabled={busy}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, dictatedNotRead: e.target.checked } : d))
                  touch()
                }}
              />
              {' '}Dictated but not read
            </label>
          </div>

          {spec.rebuilds_on_save && (
            <p className="banner banner-warn">
              Saving this form rebuilds the text below from the ticked boxes and
              discards anything typed here — that is what
              <code> {spec.legacy_page}</code> does on every save.
            </p>
          )}

          <div className="field">
            <label htmlFor="ip-interpretation">Interpretation</label>
            <textarea
              id="ip-interpretation"
              className="narrative"
              rows={16}
              value={draft.interpretation}
              disabled={busy || spec.rebuilds_on_save}
              onChange={(e) => {
                setDraft((d) => (d ? { ...d, interpretation: e.target.value } : d))
                touch()
              }}
            />
          </div>
        </fieldset>

        <div className="actions ws-actions">
          <button type="submit" disabled={busy || !dirty}>
            {busy ? 'Working…' : 'Submit'}
          </button>
          {hasChecks && !spec.rebuilds_on_save && (
            <button type="button" disabled={busy} onClick={() => void runBuild()}>
              Build
            </button>
          )}
          {/* The legacy's own captions: Clear where there are boxes to untick,
              Reset on carotid where there are not. */}
          {hasChecks ? (
            <button type="button" disabled={busy} onClick={clearChecks}>
              Clear
            </button>
          ) : (
            <button type="button" disabled={busy} onClick={resetInterpretation}>
              Reset
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              if (leave(e)) navigate(exit)
            }}
          >
            Cancel
          </button>
          <span className="subtle">
            {dirty
              ? 'Unsaved changes.'
              : 'No changes. Nothing is written until something changes.'}
          </span>
        </div>
      </form>
    </main>
  )
}

function CheckRow({
  item, checked, text, disabled, onCheck, onText,
}: {
  item: InterpretationItem
  checked: boolean
  text: string
  disabled: boolean
  onCheck: (v: boolean) => void
  onText: (v: string) => void
}) {
  return (
    <div className="field field-check">
      <label title={item.tooltip}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheck(e.target.checked)}
        />
        {' '}{item.label}
        {/* The legacy renders these and then never stores them: reopening the
            form shows them clear while their sentence stays in the report. */}
        {item.column === null && (
          <span className="muted" title={`${item.name} has no column; the legacy does not persist it.`}>
            {' '}· not stored
          </span>
        )}
      </label>
      {item.textbox && (
        <input
          type="text"
          value={text}
          disabled={disabled}
          placeholder="appended to this sentence — not stored"
          onChange={(e) => onText(e.target.value)}
        />
      )}
    </div>
  )
}

function SaveSummary({ result }: { result: InterpretationSaved }) {
  const keys = Object.keys(result.changed)
  if (keys.length === 0) {
    return <p className="banner banner-ok">Nothing to save — the interpretation is unchanged.</p>
  }
  return (
    <div className="banner banner-ok">
      <strong>Saved.</strong>
      {result.created_interpretation &&
        ` Created interpretation row #${result.interpretation_id}.`}
      {result.status_reason && ` ${result.status_reason}`}
      <ul className="save-summary">
        {keys.map((k) => (
          <li key={k}>{k}</li>
        ))}
      </ul>
    </div>
  )
}
